import { Users, Target, Award, Linkedin, Mail, ArrowRight } from "lucide-react"
import Header from "@/components/Header"
import Footer from "@/components/Footer"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { useLanguage } from "@/hooks/useLanguage"
import { SEO } from "@/components/SEO"

const AboutUs = () => {
  const { t } = useLanguage()
  const text = t.aboutUs

  return (
    <div className="min-h-screen bg-background">
      <SEO title={t.seo.aboutTitle} description={t.seo.aboutDesc} />
      <Header />

      <main>
        {/* Modern Industrial Hero */}
        <section className="relative flex h-[60vh] items-center justify-center overflow-hidden border-b-[1px] border-foreground/10">
          <div className="absolute inset-0 bg-muted/30" />
          <div className="container relative z-10 mx-auto px-4">
            <div className="flex flex-col items-center text-center">
              <span className="mb-4 text-xs font-black uppercase tracking-[0.4em] text-primary">
                {text.subtitle}
              </span>
              <h1 className="font-display text-7xl tracking-tighter text-foreground md:text-9xl">
                {text.title}
              </h1>
            </div>
          </div>
        </section>

        {/* Mission & Vision Section - Asymmetric Layout */}
        <section className="border-b-[1px] border-foreground/5 py-32">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 items-start gap-16 lg:grid-cols-12">
              <div className="space-y-12 lg:col-span-5">
                <div className="space-y-6">
                  <h2 className="font-display text-5xl uppercase tracking-widest text-primary">
                    {text.mission}
                  </h2>
                  <p className="text-2xl font-medium italic tracking-tight text-foreground">
                    "{text.missionText}"
                  </p>
                </div>
                <div className="h-[1px] w-24 bg-foreground/20" />
              </div>

              <div className="hidden h-full items-center justify-center lg:col-span-2 lg:flex">
                <div className="h-full w-px bg-foreground/10" />
              </div>

              <div className="space-y-12 lg:col-span-5 lg:pt-24">
                <div className="space-y-6">
                  <h2 className="font-display text-5xl uppercase tracking-widest text-foreground">
                    {text.vision}
                  </h2>
                  <p className="text-xl font-medium text-muted-foreground">
                    {text.visionText}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats / Highlight Section */}
        <section className="overflow-hidden bg-foreground py-24 text-background dark:bg-[#121212] dark:text-foreground">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 gap-12 md:grid-cols-3">
              <div className="group cursor-default space-y-2">
                <div className="font-display text-6xl tracking-tighter transition-colors group-hover:text-primary">
                  15+
                </div>
                <div className="text-xs font-black uppercase tracking-subtitle text-background/50 dark:text-muted-foreground/50">
                  {text.experience}
                </div>
              </div>
              <div className="group cursor-default space-y-2">
                <div className="font-display text-6xl tracking-tighter transition-colors group-hover:text-primary">
                  200+
                </div>
                <div className="text-xs font-black uppercase tracking-subtitle text-background/50 dark:text-muted-foreground/50">
                  {text.processors}
                </div>
              </div>
              <div className="group cursor-default space-y-2">
                <div className="font-display text-6xl tracking-tighter transition-colors group-hover:text-primary">
                  100%
                </div>
                <div className="text-xs font-black uppercase tracking-subtitle text-background/50 dark:text-muted-foreground/50">
                  {text.transparency}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values - Layered Industrial Style */}
        <section className="bg-muted/30 py-32">
          <div className="container mx-auto px-4">
            <h2 className="mb-24 text-center font-display text-6xl uppercase tracking-widest text-foreground">
              {text.values}
            </h2>

            <div className="mx-auto grid max-w-7xl grid-cols-1 gap-px overflow-hidden border-[1px] border-foreground/10 bg-foreground/10 shadow-2xl md:grid-cols-3">
              <div className="group relative space-y-8 bg-card p-16 transition-all hover:bg-foreground hover:text-background dark:hover:bg-[#121212] dark:hover:text-foreground">
                <div className="flex items-start justify-between">
                  <div className="flex h-20 w-20 items-center justify-center border-[1px] border-foreground/10 bg-muted transition-all duration-500 group-hover:border-primary group-hover:bg-primary">
                    <Target className="h-10 w-10 text-primary transition-colors group-hover:text-primary-foreground" />
                  </div>
                  <span className="font-display text-4xl opacity-5 transition-opacity group-hover:opacity-10">
                    01
                  </span>
                </div>
                <h4 className="font-display text-3xl uppercase tracking-widest">
                  {text.efficiency}
                </h4>
                <p className="text-lg font-medium text-muted-foreground transition-colors group-hover:text-white/70">
                  {text.efficiencyDesc}
                </p>
              </div>

              <div className="group relative space-y-8 bg-card p-16 transition-all hover:bg-foreground hover:text-background dark:hover:bg-[#121212] dark:hover:text-foreground">
                <div className="flex items-start justify-between">
                  <div className="flex h-20 w-20 items-center justify-center border-[1px] border-foreground/10 bg-muted transition-all duration-500 group-hover:border-primary group-hover:bg-primary">
                    <Award className="h-10 w-10 text-primary transition-colors group-hover:text-primary-foreground" />
                  </div>
                  <span className="font-display text-4xl opacity-5 transition-opacity group-hover:opacity-10">
                    02
                  </span>
                </div>
                <h4 className="font-display text-3xl uppercase tracking-widest">
                  {text.accessibility}
                </h4>
                <p className="text-lg font-medium text-muted-foreground transition-colors group-hover:text-white/70">
                  {text.accessibilityDesc}
                </p>
              </div>

              <div className="group relative space-y-8 bg-card p-16 transition-all hover:bg-foreground hover:text-background dark:hover:bg-[#121212] dark:hover:text-foreground">
                <div className="flex items-start justify-between">
                  <div className="flex h-20 w-20 items-center justify-center border-[1px] border-foreground/10 bg-muted transition-all duration-500 group-hover:border-primary group-hover:bg-primary">
                    <Users className="h-10 w-10 text-primary transition-colors group-hover:text-primary-foreground" />
                  </div>
                  <span className="font-display text-4xl opacity-5 transition-opacity group-hover:opacity-10">
                    03
                  </span>
                </div>
                <h4 className="font-display text-3xl uppercase tracking-widest">
                  {text.innovation}
                </h4>
                <p className="text-lg font-medium text-muted-foreground transition-colors group-hover:text-white/70">
                  {text.innovationDesc}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-32">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-5xl border-[1px] border-foreground/10 bg-muted/30 p-1">
              <div className="flex flex-col items-center justify-between gap-12 bg-card p-12 md:flex-row md:p-24">
                <div className="max-w-xl">
                  <h2 className="mb-6 font-display text-5xl tracking-tighter md:text-6xl">
                    {text.readyToModernize}
                  </h2>
                  <p className="text-xs font-black uppercase tracking-[0.2em] text-muted-foreground">
                    {text.joinLeadingMarketplace}
                  </p>
                </div>
                <Button
                  className="h-auto rounded-none bg-action px-12 py-8 font-display text-3xl tracking-widest text-action-foreground shadow-2xl transition-all hover:scale-[1.01] hover:brightness-110 active:scale-95"
                  onClick={() => (window.location.href = "/contact")}
                >
                  {text.contactUs}
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  )
}

export default AboutUs
